import type { Attendee, Group } from '../../lib/types';
import { matchesSearchQuery } from '../attendees-list.utils';

export type RosterPerGroupEntry = {
  group: Group;
  attendees: Attendee[];
};

export type FilteredRoster = {
  perGroup: RosterPerGroupEntry[];
  unassigned: Attendee[];
  totalMatches: number;
};

// Filters the Roster's `perGroup` + `unassigned` breakdown against a
// name search query (delegates to `matchesSearchQuery`, so it matches
// both "First Last" and "Last, First" case-insensitively).
//
// Cards with zero matches are dropped from the returned `perGroup` and
// `unassigned` is replaced by its filtered list — the caller can then
// render only the cards that contain hits and surface a "no matches"
// empty state via `totalMatches === 0`.
//
// An empty / whitespace-only query is a passthrough: original arrays
// are returned by reference (so `useMemo` consumers don't see a fresh
// identity on every render), and `totalMatches` is the unfiltered total.
export const filterRosterByQuery = (
  perGroup: RosterPerGroupEntry[],
  unassigned: Attendee[],
  query: string,
): FilteredRoster => {
  const lowerQuery = query.trim().toLowerCase();
  if (!lowerQuery) {
    const totalMatches =
      unassigned.length +
      perGroup.reduce((n, { attendees }) => n + attendees.length, 0);
    return { perGroup, unassigned, totalMatches };
  }
  const filteredUnassigned = unassigned.filter((a) =>
    matchesSearchQuery(a, lowerQuery),
  );
  const filteredPerGroup = perGroup
    .map(({ group, attendees }) => ({
      group,
      attendees: attendees.filter((a) => matchesSearchQuery(a, lowerQuery)),
    }))
    .filter(({ attendees }) => attendees.length > 0);
  const totalMatches =
    filteredUnassigned.length +
    filteredPerGroup.reduce((n, { attendees }) => n + attendees.length, 0);
  return {
    perGroup: filteredPerGroup,
    unassigned: filteredUnassigned,
    totalMatches,
  };
};
